/**
 * Domain-Enums.
 *
 * Diese Enums sind die Single-Source-of-Truth für Status-Werte.
 * Drizzle-pgEnums werden in `@hirebotiq-ops/infra` aus diesen abgeleitet,
 * damit DB-Schema und App-Code nicht driften.
 */

export const ROLES = ['pending', 'ceo', 'internal_staff', 'worker', 'client'] as const;
export type OpRole = (typeof ROLES)[number];

export const WORKER_STATUSES = [
  'draft',
  'pending_review',
  'approved',
  'blocked',
  'rejected',
  'terminated',
] as const;
export type WorkerStatus = (typeof WORKER_STATUSES)[number];

export const CLIENT_STATUSES = [
  'draft',
  'pending_review',
  'approved',
  'blocked',
  'rejected',
] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const ASSIGNMENT_STATUSES = [
  'draft',
  'pending_review',
  'active',
  'ended',
  'cancelled',
] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

export const ABSENCE_TYPES = ['vacation', 'sick', 'special', 'unpaid'] as const;
export type AbsenceType = (typeof ABSENCE_TYPES)[number];

export const ABSENCE_STATUSES = ['requested', 'approved', 'rejected', 'cancelled'] as const;
export type AbsenceStatus = (typeof ABSENCE_STATUSES)[number];

export const TIMESHEET_STATUSES = [
  'draft',
  'submitted',
  // Entleiher/Kunde hat die Stunden bestätigt; wartet auf finale Agentur-Freigabe.
  'client_approved',
  // Entleiher hat Zeiten korrigiert; wartet auf Ein-Klick-Bestätigung des Workers.
  'client_corrected',
  // Worker widerspricht der Kunden-Korrektur; Staff-Klärungsfall (Override-Pfad).
  'disputed',
  'approved',
  'rejected',
] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

/**
 * Ablehnungs-Kategorien (Pflicht bei jeder Ablehnung). Die Kategorie steuert
 * den Folgepfad:
 * - `zeiten_fehlerhaft`   → Korrektur-Schleife (Kunde korrigiert oder Worker bessert nach)
 * - `einsatz_nicht_erfolgt` → Klärung durch Staff, kein Lohn ohne Klärung
 * - `vertragsklausel`     → Stunden bleiben anerkannt (Lohn läuft), aber
 *                            nicht abrechenbar (`billable=false`), z. B. 3-Tage-Frist
 * - `sonstiges`           → Staff-Klärung, Freitext-Pflicht
 */
export const TIMESHEET_REJECT_CATEGORIES = [
  'zeiten_fehlerhaft',
  'einsatz_nicht_erfolgt',
  'vertragsklausel',
  'sonstiges',
] as const;
export type TimesheetRejectCategory = (typeof TIMESHEET_REJECT_CATEGORIES)[number];

/**
 * Staff kann fachliche Fehler ablehnen. `vertragsklausel` bleibt bewusst dem
 * Kundenpfad vorbehalten: dort werden die Stunden anerkannt und lediglich als
 * nicht abrechenbar markiert. Staff finalisiert diesen Zettel anschließend
 * regulär aus `client_approved` und kann die Kundenstufe nicht umgehen.
 */
export const STAFF_TIMESHEET_REJECT_CATEGORIES = [
  'zeiten_fehlerhaft',
  'einsatz_nicht_erfolgt',
  'sonstiges',
] as const;

/** Herkunft der erfassten Zeiten — bestimmt, welche Seite bestätigen muss. */
export const TIMESHEET_SOURCES = ['worker_entry', 'client_system', 'staff_entry'] as const;
export type TimesheetSource = (typeof TIMESHEET_SOURCES)[number];

/**
 * GVP-DGB-Entgeltgruppen (Zeitarbeit-Tarif) — bewusst identisch zum
 * Calculator (`Landingpage/apps/calculator/shared/qualifications.ts`),
 * damit beide Systeme dasselbe Vokabular sprechen.
 */
export const ENTGELT_GROUPS = [
  'eg1', 'eg2a', 'eg2b', 'eg3', 'eg4', 'eg5', 'eg6', 'eg7', 'eg8', 'eg9',
] as const;
export type EntgeltGroup = (typeof ENTGELT_GROUPS)[number];

/** Bundesländer (ISO 3166-2:DE) — Feiertage gelten am EINSATZORT. */
export const GERMAN_STATES = [
  'BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV',
  'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH',
] as const;
export type GermanState = (typeof GERMAN_STATES)[number];

/** Herkunft des Verrechnungssatzes am Einsatz: aus Kundenmatrix geerbt oder manuell. */
export const RATE_SOURCES = ['matrix', 'manual'] as const;
export type RateSource = (typeof RATE_SOURCES)[number];

/** Tageszeilen-Typ: Arbeitsintervall oder Pause (Lage zählt, nicht nur Dauer). */
export const TIMESHEET_ENTRY_TYPES = ['work', 'break'] as const;
export type TimesheetEntryType = (typeof TIMESHEET_ENTRY_TYPES)[number];

export const DOCUMENT_TYPES = [
  // Worker
  'id_card',
  'passport',
  'residence_permit',
  'work_permit',
  'social_security',
  'tax_certificate',
  'health_insurance',
  'bank_certificate',
  'qualification',
  'driving_license',
  'health_certificate',
  'disability_certificate',
  'criminal_record',
  // Client
  'client_company_register',
  'client_vat_certificate',
  'client_risk_assessment',
  // Generic
  'other',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_STATUSES = [
  'uploaded',
  'pending_review',
  'approved',
  'rejected',
  'expired',
  'deleted',
] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const INSURANCE_STATUSES = ['gkv', 'pkv'] as const;
export type InsuranceStatus = (typeof INSURANCE_STATUSES)[number];

export const MARITAL_STATUSES = [
  'single',
  'married',
  'divorced',
  'widowed',
  'civil_union',
] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'submit',
  'approve',
  'reject',
  'block',
  'unblock',
  'role_assign',
  'document_upload',
  'document_review',
  'document_delete',
  'event_processed',
  'impersonate_start',
  'impersonate_stop',
  // Staff-Freigabe unter Umgehung der Kundenbestätigung — nur mit Pflicht-Begründung.
  'override_approve',
  // Entleiher korrigiert eingereichte Zeiten (Ursprungswerte bleiben erhalten).
  'client_correct',
  // Worker bestätigt die Kunden-Korrektur (Ein-Klick).
  'confirm_correction',
  // Worker widerspricht der Kunden-Korrektur → Staff-Klärungsfall.
  'dispute_correction',
  // Worker reicht einen abgelehnten Zettel korrigiert neu ein.
  'resubmit',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const TARGET_TYPES = [
  'worker',
  'client',
  'document',
  'user',
  'assignment',
  'absence',
  'timesheet',
  // Persistierte Zuschlags-Regelwerke (OPS-69) — Finanz-Stammdaten.
  'surcharge_rule_set',
  // Branchenzuschlags-/Preis-Staffeln nach Einsatzdauer (OPS-69).
  'surcharge_ladder',
  // Manuell gepflegte Fristen/Stichtage (OPS-73).
  'deadline',
  'settings',
] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

export const OWNER_TYPES = ['worker', 'client'] as const;
export type OwnerType = (typeof OWNER_TYPES)[number];

export const COMPLIANCE_FLAG_SEVERITIES = ['info', 'warning', 'blocker'] as const;
export type ComplianceFlagSeverity = (typeof COMPLIANCE_FLAG_SEVERITIES)[number];
